import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, X, ShieldAlert, HelpCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/** Help — Yubo-style: plain full-width rows on soft grey blocks. */
export default function SettingsHelp() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(null); // null | "question" | "safety_concern"
  const [showTickets, setShowTickets] = useState(false);

  const rows = [
    { key: "question", label: t("settings.helpQuestion"), onClick: () => setForm("question") },
    { key: "report", label: t("settings.helpReport"), onClick: () => setForm("safety_concern") },
    { key: "mytickets", label: t("settings.helpMyTickets"), onClick: () => setShowTickets(true) },
    { key: "safetyhub", label: t("settings.helpSafetyHub"), onClick: () => nav("/settings/help/safety-hub") },
    { key: "privacy", label: t("settings.helpMyPrivacy"), onClick: () => nav("/settings/privacy") },
    ...(user?.role === "admin" ? [{ key: "admintickets", label: t("settings.helpAdminTickets"), onClick: () => nav("/admin/tickets") }] : []),
  ];

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="settings-help-page">
      <div className="sticky top-0 z-30 bg-white">
        <div className="flex items-center gap-4 px-5 py-5">
          <button onClick={() => nav(-1)} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]" data-testid="help-back" aria-label="Back">
            <ArrowLeft className="w-7 h-7" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[28px] tracking-tight">{t("settings.help")}</h1>
        </div>
      </div>

      <div className="mt-2 space-y-3">
        {rows.map((r) => (
          <button
            key={r.key}
            onClick={r.onClick}
            className="w-full bg-[#F5F5F3] hover:bg-[#EDEDE9] transition-colors text-left px-6 py-7 text-lg font-medium"
            data-testid={`help-${r.key}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {form && <TicketModal t={t} type={form} onClose={() => setForm(null)} />}
      {showTickets && <MyTicketsSheet t={t} onClose={() => setShowTickets(false)} />}
    </div>
  );
}

/** Bottom sheet listing the user's own support tickets + admin replies. */
function MyTicketsSheet({ t, onClose }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/support/tickets/mine")
      .then(({ data }) => setItems(data))
      .catch((e) => { toast.error(fmtError(e.response?.data?.detail) || e.message); setItems([]); });
  }, []);

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="my-tickets-sheet">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-16 sm:pb-6 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-black text-xl">{t("settings.helpMyTickets")}</h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F4F1E8] hover:bg-[#E9E5D7]" data-testid="my-tickets-close" aria-label="Close">
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {items === null ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-[#7C828D]" data-testid="my-tickets-empty">{t("settings.helpNoTickets")}</p>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} className="bg-[#F5F5F3] rounded-2xl p-4" data-testid={`my-ticket-${it.id}`}>
                <div className="flex items-center gap-2 flex-wrap text-xs font-bold">
                  {it.type === "safety_concern"
                    ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FFE6E0]"><ShieldAlert className="w-3.5 h-3.5" strokeWidth={2.5}/>{t("adminTickets.safetyConcern")}</span>
                    : <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#DDF5EA]"><HelpCircle className="w-3.5 h-3.5" strokeWidth={2.5}/>{t("adminTickets.question")}</span>}
                  <span className={`px-2 py-1 rounded-full ${it.status === "open" ? "bg-[#FFF1D6]" : "bg-[#DDF5EA] text-[#0F9D8F]"}`}>
                    {it.status === "open" ? t("adminTickets.open") : t("adminTickets.answered")}
                  </span>
                  <span className="ms-auto font-mono font-normal text-[#7C828D]">{it.created_at?.slice(0, 10)}</span>
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap">{it.message}</p>
                {it.reply && (
                  <div className="mt-3 rounded-xl border-2 border-[#14C8B0] bg-[#DDF5EA] p-3" data-testid={`my-ticket-reply-${it.id}`}>
                    <div className="flex items-center gap-1.5 text-xs font-display font-bold text-[#0F9D8F] mb-1">
                      <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />
                      {t("settings.helpTicketAnswer")}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{it.reply.message}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const CATEGORIES = ["harassment", "spam", "fake_profile", "inappropriate_content", "other"];

/** Contact form for "I have a question" / "Report a Safety Concern". */
function TicketModal({ t, type, onClose }) {
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const isReport = type === "safety_concern";

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/support/tickets", {
        type,
        category: isReport ? (category || "other") : null,
        message: message.trim(),
      });
      toast.success(t("settings.helpSent"));
      onClose();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="help-ticket-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-16 sm:pb-6 shadow-2xl">
        <h2 className="font-display font-black text-xl mb-1">
          {isReport ? t("settings.helpReport") : t("settings.helpQuestion")}
        </h2>
        <p className="text-sm text-[#7C828D] mb-4">{t("settings.helpFormHint")}</p>

        {isReport && (
          <div className="flex flex-wrap gap-2 mb-4">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 transition-colors ${
                  category === c ? "bg-[#00CED1]/15 border-[#00CED1]" : "bg-[#F4F1E8] border-transparent hover:bg-[#E9E5D7]"
                }`}
                data-testid={`help-cat-${c}`}
              >
                {t(`settings.cat_${c}`)}
              </button>
            ))}
          </div>
        )}

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={4000}
          rows={5}
          placeholder={t("settings.helpMessagePlaceholder")}
          className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7] resize-none mb-4"
          data-testid="help-message-input"
          autoFocus
        />

        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy} className="flex-1 bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black font-bold py-3 rounded-2xl" data-testid="help-cancel">
            {t("common.cancel")}
          </button>
          <button
            onClick={send}
            disabled={busy || message.trim().length < 5}
            className="flex-1 bg-[#00CED1] text-black font-black py-3 rounded-2xl border-2 border-[#121212] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="help-send"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("settings.helpSend")}
          </button>
        </div>
      </div>
    </div>
  );
}
